import React from "react";
import {
  List,
  Card,
  Button,
  Spin,
  Image,
  message,
  Tag,
  Empty,
  Avatar,
  Tooltip,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  UpOutlined,
  DownOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { connect } from "react-redux";

import Base from "../../Base";
import { getUserInfo } from "../../../utils/redux/reducers/User";
import EditProductGroupForm from "./EditProductGroupForm";
import "./HomeProductGroups.scss";

const MAX_GROUPS = 20;
const MAX_THUMBS = 5;

class HomeProductGroupsManagement extends Base {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      groups: [],
      groupData: null,
      loading: false,
    };
  }

  componentDidMount() {
    super.componentDidMount();
    this.loadGroups();
  }

  loadGroups = async () => {
    const token = this.getUserToken();
    if (!token) return;

    const api = this.api.merchant.getProductGroups;
    const apiConfig = {
      token,
      query: { page_type: 0 },
    };

    try {
      this.setState({ loading: true });
      const res = await api.run(apiConfig);
      if (!this.mounted) return;
      if (res.result && Array.isArray(res.data)) {
        this.setState({ groups: res.data });
      } else {
        this.setState({ groups: [] });
        if (!res.result) this.showMessage(res.message || "Failed to load", Base.Message.error);
      }
    } catch (error) {
      if (!this.mounted) return;
      this.handleApiError(api, error);
    } finally {
      if (!this.mounted) return;
      this.setState({ loading: false, groupData: null });
    }
  };

  putGroups = async (groups) => {
    const token = this.getUserToken();
    if (!token) return false;

    if (groups.length > MAX_GROUPS) {
      this.showMessage(this.i18n.t("homeProductGroups.maxGroupsError"), Base.Message.error);
      return false;
    }

    const normalized = groups.map((g) => {
      const isFlashsale = !!g.is_flashsale;
      return {
        ...(g.id != null ? { id: g.id } : {}),
        ug_name: g.ug_name ?? null,
        zh_name: g.zh_name ?? null,
        ug_title_image: g.ug_title_image ?? null,
        zh_title_image: g.zh_title_image ?? null,
        list_type: [0, 1, 2].includes(Number(g.list_type)) ? Number(g.list_type) : 0,
        is_flashsale: isFlashsale,
        items: isFlashsale
          ? []
          : (g.items || []).map((it) => ({
              product_id: Number(it.product_id),
            })),
      };
    });

    const api = this.api.merchant.updateProductGroups;
    const apiConfig = {
      token,
      query: { page_type: 0 },
      data: { groups: normalized },
    };

    try {
      this.setState({ loading: true });
      const res = await api.run(apiConfig);
      if (!this.mounted) return false;
      if (res.result && Array.isArray(res.data)) {
        this.setState({ groups: res.data });
        message.success(this.i18n.t("homeProductGroups.saveSuccess"));
        return true;
      }
      const msg = res.message || this.i18n.t("base.api.errUnknown");
      this.showMessage(msg, Base.Message.error);
      return false;
    } catch (error) {
      if (!this.mounted) return false;
      const err = error.response || error;
      const status = err.status ?? err.response?.status;
      if (status === 422) {
        this.onApi422Error(api, err.response ? err : { data: err.data || err, status: 422 });
      } else if (err.message) {
        this.showMessage(err.message, Base.Message.error);
      } else {
        this.handleApiError(api, err);
      }
      return false;
    } finally {
      if (!this.mounted) return false;
      this.setState({ loading: false });
    }
  };

  onHandleCancel = () => {
    this.setState({ groupData: null });
  };

  onAddGroup = () => {
    if (this.state.groups.length >= MAX_GROUPS) {
      this.showMessage(this.i18n.t("homeProductGroups.maxGroupsError"), Base.Message.error);
      return;
    }
    this.setState({ groupData: { items: [], is_flashsale: false } });
  };

  onEditGroup = (group) => {
    this.setState({
      groupData: {
        ...group,
        items: group.items ? [...group.items] : [],
      },
    });
  };

  onDeleteGroup = (group) => {
    this.showModal(
      this.i18n.t("sales.deleteWarning"),
      Base.Modal.confirm,
      async () => {
        const { groups } = this.state;
        const next = groups.filter((g) => g.id !== group.id);
        await this.putGroups(next);
      }
    );
  };

  moveGroup = (index, direction) => {
    const { groups } = this.state;
    const next = [...groups];
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    this.putGroups(next);
  };

  onFinishForm = async (groupPayload) => {
    const { groups, groupData } = this.state;
    let next;
    if (groupData && groupData.id != null) {
      next = groups.map((g) => (g.id === groupData.id ? { ...groupPayload, id: groupData.id } : g));
    } else {
      next = [...groups, groupPayload];
    }
    const success = await this.putGroups(next);
    if (success) {
      this.setState({ groupData: null });
    }
  };

  renderCardCover(item, index) {
    const zh = item.zh_title_image;
    const ug = item.ug_title_image;
    return (
      <div className="home-product-groups-card__cover">
        <span className="home-product-groups-card__order">{index + 1}</span>
        {zh || ug ? (
          <>
            {zh ? (
              <Image
                src={zh}
                alt=""
                className="home-product-groups-card__cover-img"
                preview={{ src: zh }}
              />
            ) : null}
            {ug ? (
              <Image
                src={ug}
                alt=""
                className="home-product-groups-card__cover-img"
                preview={{ src: ug }}
              />
            ) : null}
          </>
        ) : (
          <div className="home-product-groups-card__cover-placeholder">
            <AppstoreOutlined />
          </div>
        )}
      </div>
    );
  }

  renderProductPreview(item) {
    if (item.is_flashsale) {
      return (
        <p className="home-product-groups-card__flash-hint">
          {this.i18n.t("homeProductGroups.flashSaleAutoProducts")}
        </p>
      );
    }

    const items = Array.isArray(item.items) ? item.items : [];
    const thumbs = items.filter((p) => p.product_image).slice(0, MAX_THUMBS);
    const extra = Math.max(0, items.length - thumbs.length);

    return (
      <div className="home-product-groups-card__products">
        {thumbs.length > 0 ? (
          <div className="home-product-groups-card__thumbs">
            {thumbs.map((p) => (
              <Avatar key={p.product_id} src={p.product_image} shape="square" size={36} />
            ))}
            {extra > 0 ? (
              <Avatar shape="square" size={36} style={{ background: "#419cb4" }}>
                {this.i18n.t("homeProductGroups.moreProducts", { count: extra })}
              </Avatar>
            ) : null}
          </div>
        ) : null}
        <span className="home-product-groups-card__product-meta">
          {this.i18n.t("homeProductGroups.productCountValue", { count: items.length })}
        </span>
      </div>
    );
  }

  renderGroupCard(item, index) {
    const { groups } = this.state;
    const title = item.zh_name || item.ug_name || `#${item.id}`;
    const subtitle =
      item.ug_name && item.zh_name && item.ug_name !== item.zh_name ? item.ug_name : null;
    const isFirst = index === 0;
    const isLast = index === groups.length - 1;

    return (
      <List.Item key={item.id || index}>
        <Card
          className="home-product-groups-card"
          hoverable
          cover={this.renderCardCover(item, index)}
          actions={[
            <Tooltip key="up" title="↑">
              <UpOutlined
                className={`home-product-groups-action${isFirst ? " is-disabled" : ""}`}
                onClick={isFirst ? undefined : () => this.moveGroup(index, "up")}
              />
            </Tooltip>,
            <Tooltip key="down" title="↓">
              <DownOutlined
                className={`home-product-groups-action${isLast ? " is-disabled" : ""}`}
                onClick={isLast ? undefined : () => this.moveGroup(index, "down")}
              />
            </Tooltip>,
            <Tooltip key="edit" title={this.i18n.t("homeProductGroups.editGroup")}>
              <EditOutlined
                className="home-product-groups-action"
                onClick={() => this.onEditGroup(item)}
              />
            </Tooltip>,
            <Tooltip key="delete" title={this.i18n.t("base.delete")}>
              <DeleteOutlined
                className="home-product-groups-action is-danger"
                onClick={() => this.onDeleteGroup(item)}
              />
            </Tooltip>,
          ]}
        >
          <div className="home-product-groups-card__title-row">
            <div>
              <h3 className="home-product-groups-card__title">{title}</h3>
              {subtitle ? (
                <p className="home-product-groups-card__subtitle">{subtitle}</p>
              ) : null}
            </div>
          </div>
          <div className="home-product-groups-card__tags">
            {item.is_flashsale ? (
              <Tag color="#419cb4" icon={<ThunderboltOutlined />}>
                {this.i18n.t("homeProductGroups.flashSaleBadge")}
              </Tag>
            ) : null}
            <Tag>
              {item.list_type === 2
                ? this.i18n.t("homeProductGroups.threePerRow")
                : item.list_type === 1
                  ? this.i18n.t("homeProductGroups.twoPerRow")
                  : this.i18n.t("homeProductGroups.onePerRow")}
            </Tag>
          </div>
          {this.renderProductPreview(item)}
        </Card>
      </List.Item>
    );
  }

  render() {
    const { groups, groupData, loading } = this.state;

    return (
      <div className="home-product-groups-page">
        <Spin spinning={loading}>
          <div className="home-product-groups-shell">
            <div className="home-product-groups-toolbar">
              <div className="home-product-groups-toolbar__meta">
                <h2 className="home-product-groups-toolbar__title">
                  {this.i18n.t("homeProductGroups.pageTitle")}
                  <Tag color="processing">
                    {this.i18n.t("homeProductGroups.groupCount", {
                      count: groups.length,
                      max: MAX_GROUPS,
                    })}
                  </Tag>
                </h2>
                <p className="home-product-groups-toolbar__hint">
                  {this.i18n.t("homeProductGroups.pageHint")}
                </p>
              </div>
              <div className="home-product-groups-toolbar__actions">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={this.onAddGroup}
                  disabled={groups.length >= MAX_GROUPS}
                >
                  {this.i18n.t("homeProductGroups.addGroup")}
                </Button>
              </div>
            </div>

            {groups.length === 0 && !loading ? (
              <div className="home-product-groups-empty">
                <Empty description={this.i18n.t("homeProductGroups.empty")}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={this.onAddGroup}>
                    {this.i18n.t("homeProductGroups.addGroup")}
                  </Button>
                </Empty>
              </div>
            ) : (
              <List
                className="home-product-groups-grid"
                grid={{
                  gutter: [16, 16],
                  xs: 1,
                  sm: 1,
                  md: 2,
                  lg: 2,
                  xl: 3,
                  xxl: 3,
                }}
                dataSource={groups}
                renderItem={(item, index) => this.renderGroupCard(item, index)}
              />
            )}
          </div>
        </Spin>

        <EditProductGroupForm
          groupData={groupData}
          onCancel={this.onHandleCancel}
          onFinish={this.onFinishForm}
          loading={loading}
          api={this.api}
          getUserToken={this.getUserToken.bind(this)}
          i18n={this.i18n}
          user={this.props.user}
        />
      </div>
    );
  }
}

export default connect((state) => ({
  user: getUserInfo(state),
}))(HomeProductGroupsManagement);
